import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { appTheme } from "./theme/theme";
import { AuthProvider } from "./auth/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import AdminRoute from "./auth/AdminRoute";
import ShellLayout from "./components/ShellLayout";
import Home from "./pages/Home";
import Categories from "./pages/Categories";
import Recipes from "./pages/Recipes";
import RecipeDetail from "./pages/RecipeDetail";
import LoginPage from "./pages/Login";
import RegisterPage from "./pages/Register";
import Profile from "./pages/Profile";
import PostDetail from "./pages/PostDetail";
import PostEditor from "./pages/PostEditor";
import GroundingJournal from "./pages/GroundingJournal";
import WineTastingJournal from "./pages/WineTastingJournal";
import WineTastingSession from "./pages/WineTastingSession";
import WineTastingDetail from "./pages/WineTastingDetail";
import WineManager from "./pages/WineManager";
import WineManagerAdd from "./pages/WineManagerAdd";
import WineManagerDetail from "./pages/WineManagerDetail";
import Finance from "./pages/Finance";
import FinanceCallback from "./pages/FinanceCallback";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import "./App.css";

function LegacyWineTastingRedirect() {
  const { tastingUuid } = useParams();
  return <Navigate to={`/wine-tasting/${tastingUuid}`} replace />;
}

function App() {
  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <SiteSettingsProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route element={<ShellLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/recipes/*" element={<RecipeDetail />} />
              <Route path="/posts/*" element={<PostDetail />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/posts"
                element={<Navigate to="/profile?tab=posts" replace />}
              />
              <Route
                path="/admin/settings"
                element={<Navigate to="/profile?tab=visibility" replace />}
              />
              <Route
                path="/admin/posts/new"
                element={
                  <AdminRoute>
                    <PostEditor />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/posts/:postUuid/edit"
                element={
                  <AdminRoute>
                    <PostEditor />
                  </AdminRoute>
                }
              />
              <Route
                path="/wine-tasting"
                element={
                  <ProtectedRoute>
                    <WineTastingJournal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wine-tasting/new"
                element={
                  <ProtectedRoute>
                    <WineTastingSession />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wine-tasting/:tastingUuid"
                element={
                  <ProtectedRoute>
                    <WineTastingDetail />
                  </ProtectedRoute>
                }
              />
              {/* Legacy /tools/wine-tasting → new paths (avoid /tools API proxy clash) */}
              <Route path="/tools/wine-tasting" element={<Navigate to="/wine-tasting" replace />} />
              <Route path="/tools/wine-tasting/new" element={<Navigate to="/wine-tasting/new" replace />} />
              <Route
                path="/tools/wine-tasting/:tastingUuid"
                element={<LegacyWineTastingRedirect />}
              />
              <Route
                path="/tools/wine-manager"
                element={
                  <ProtectedRoute>
                    <WineManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/wine-manager/add"
                element={
                  <ProtectedRoute>
                    <WineManagerAdd />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/wine-manager/bottle/:userWineUuid"
                element={
                  <ProtectedRoute>
                    <WineManagerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/wine-manager/:userWineUuid"
                element={
                  <ProtectedRoute>
                    <WineManagerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grounding"
                element={
                  <AdminRoute>
                    <GroundingJournal />
                  </AdminRoute>
                }
              />
              <Route
                path="/finance"
                element={
                  <AdminRoute>
                    <Finance />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/finance/callback"
                element={
                  <AdminRoute>
                    <FinanceCallback />
                  </AdminRoute>
                }
              />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </SiteSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
